<template>
    <!-- ✅ DIALOG DE PROGRESSO DA ANÁLISE -->
    <v-dialog v-model="dialogProgresso" fullscreen persistent >
        <v-card class="overflow-hidden">
            <!-- ✅ HEADER ANIMADO -->
            <v-card-title class="bg-gradient-primary text-white pa-6">
                <div class="d-flex align-center">
                    <v-progress-circular :model-value="progressoPercentual" :rotate="360" :size="50" :width="4"
                        color="white" class="mr-4">
                        <template v-slot:default>
                            <v-icon color="white" size="24">
                                {{ progressoPercentual === 100 ? 'mdi-check' : 'mdi-chart-line' }}
                            </v-icon>
                        </template>
                    </v-progress-circular>

                    <div>
                        <div class="text-h5 font-weight-bold">
                            <ProgramacaoLocalizador codigo="L392" />
                            {{ progressoPercentual === 100 ? 'Análise Concluída!' : 'Analisando Ordens' }}
                        </div>
                        <div class="text-subtitle-1 opacity-90">
                            {{ setorSelecionado?.nome || 'Setor' }}
                            <span v-if="tempoExecucao" class="ml-2 text-white text-caption">
                                <v-icon size="small" color="white">mdi-clock-outline</v-icon>
                                {{ tempoExecucao }}
                            </span>
                        </div>
                    </div>
                </div>
            </v-card-title>

            <!-- ✅ CONTEÚDO DO PROGRESSO -->
            <v-card-text class="pa-6">
                <!-- ✅ PROGRESSO GERAL -->
                <div class="mb-6">
                    <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-h6 font-weight-bold">Progresso Geral</span>
                        <span class="text-h6 text-primary font-weight-bold">
                            {{ progressoAtual }}/{{ progressoTotal }}
                        </span>
                    </div>

                    <v-progress-linear :model-value="progressoPercentual" height="12" rounded striped
                        :color="progressoPercentual === 100 ? 'success' : 'primary'" class="mb-2">
                        <template v-slot:default>
                            <strong class="text-white text-caption">
                                {{ progressoPercentual }}%
                            </strong>
                        </template>
                    </v-progress-linear>

                    <div class="text-caption text-grey text-center">
                        {{ mensagemProgressoDetalhada }}
                    </div>
                </div>

                <!-- ✅ ORDEM ATUAL SENDO PROCESSADA -->
                <div class="mb-6" v-if="ordemAtualProcessando && progressoPercentual < 100">
                    <v-card variant="outlined" class="pa-4">
                        <div class="d-flex align-center">
                            <v-avatar color="primary" size="40" class="mr-3">
                                <v-icon color="white">mdi-cog</v-icon>
                            </v-avatar>
                            <div class="flex-grow-1">
                                <div class="font-weight-bold text-subtitle-1">
                                    Processando: {{ ordemAtualProcessando.ordemTexto }}
                                </div>
                                <div class="text-caption text-grey">
                                    Parte: {{ ordemAtualProcessando.parte }} | ID: {{ ordemAtualProcessando.ordemId }}
                                </div>
                            </div>
                            <v-progress-circular indeterminate color="primary" size="24"
                                width="3"></v-progress-circular>
                        </div>
                    </v-card>
                </div>

                <!-- ✅ ESTATÍSTICAS EM TEMPO REAL - ATUALIZADO COM NOVO STATUS -->
                <div class="mb-6">
                    <div class="text-h6 font-weight-bold mb-3">Resultados Parciais</div>
                    <div class="d-flex gap-2 justify-center" style="flex-wrap: nowrap; overflow-x: auto;">
                        <!-- ✅ CONCLUÍDAS -->
                        <v-card variant="tonal" color="success" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="success" size="20" class="mb-1">mdi-check-circle</v-icon>
                            <div class="text-h6 font-weight-bold text-success">
                                {{ estatisticasTemp.concluidas }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Concluídas</div>
                        </v-card>

                        <!-- ✅ EM ANDAMENTO -->
                        <v-card variant="tonal" color="warning" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="warning" size="20" class="mb-1">mdi-clock</v-icon>
                            <div class="text-h6 font-weight-bold text-warning">
                                {{ estatisticasTemp.emAndamento }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Em Andamento</div>
                        </v-card>

                        <!-- ✅ PENDENTES -->
                        <v-card variant="tonal" color="error" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="error" size="20" class="mb-1">mdi-alert-circle</v-icon>
                            <div class="text-h6 font-weight-bold text-error">
                                {{ estatisticasTemp.pendentes }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Pendentes</div>
                        </v-card>

                        <!-- ✅ NOVO: PENDENTE DE AUTORIZAÇÃO TERCEIRO -->
                        <v-card variant="tonal" color="purple" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="purple" size="20" class="mb-1">mdi-account-clock</v-icon>
                            <div class="text-h6 font-weight-bold text-purple">
                                {{ estatisticasTemp.pendenteAutorizacaoTerceiro }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Pend. Autorização</div>
                        </v-card>

                        <!-- ✅ SEM MOVIMENTAÇÃO -->
                        <v-card variant="tonal" color="blue-grey" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="blue-grey" size="20" class="mb-1">mdi-pause-circle</v-icon>
                            <div class="text-h6 font-weight-bold text-blue-grey">
                                {{ estatisticasTemp.semMovimentacao }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Sem Movimentação</div>
                        </v-card>

                        <!-- ✅ SEM DADOS -->
                        <v-card variant="tonal" color="info" class="text-center pa-2 flex-shrink-0"
                            style="min-width: 100px; max-width: 120px;">
                            <v-icon color="info" size="20" class="mb-1">mdi-database-off</v-icon>
                            <div class="text-h6 font-weight-bold text-info">
                                {{ estatisticasTemp.semDados }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Sem Dados</div>
                        </v-card>

                        <!-- ✅ COM ERRO (CONDICIONAL) -->
                        <v-card v-if="estatisticasTemp.erros > 0" variant="tonal" color="error"
                            class="text-center pa-2 flex-shrink-0" style="min-width: 100px; max-width: 120px;">
                            <v-icon color="error" size="20" class="mb-1">mdi-alert-circle</v-icon>
                            <div class="text-h6 font-weight-bold text-error">
                                {{ estatisticasTemp.erros }}
                            </div>
                            <div class="text-caption" style="font-size: 10px;">Com Erro</div>
                        </v-card>
                    </div>
                </div>

                <!-- ✅ LOG DE ATIVIDADES -->
                <div class="mb-4" v-if="logAtividades.length > 0">
                    <div class="text-h6 font-weight-bold mb-3">Últimas Atividades</div>
                    <v-card variant="outlined" max-height="150" class="overflow-y-auto">
                        <v-list density="compact">
                            <v-list-item v-for="(log, index) in logAtividades.slice(-5)" :key="index"
                                class="text-caption">
                                <template v-slot:prepend>
                                    <v-icon size="16" :color="getLogColor(log.tipo)">
                                        {{ getLogIcon(log.tipo) }}
                                    </v-icon>
                                </template>
                                <v-list-item-title class="text-caption">
                                    {{ log.mensagem }}
                                </v-list-item-title>
                                <template v-slot:append>
                                    <span class="text-caption text-grey">
                                        {{ log.hora }}
                                    </span>
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </div>

                <!-- ✅ TEMPO ESTIMADO -->
                <div class="text-center" v-if="progressoPercentual < 100 && tempoEstimado">
                    <v-chip color="info" variant="outlined">
                        <v-icon start>mdi-clock-outline</v-icon>
                        Tempo estimado: {{ tempoEstimado }}
                    </v-chip>
                </div>
            </v-card-text>

            <!-- ✅ FOOTER COM AÇÕES -->
            <v-card-actions class="pa-6 pt-0" v-if="progressoPercentual === 100">
                <v-spacer></v-spacer>
                <v-btn color="success" variant="elevated" size="large" @click="abrirResumo" class="px-8">
                    <v-icon start>mdi-eye</v-icon>
                    Ver Resultados
                </v-btn>
<!-- criar o botao pra fechar -->
                <v-btn color="error" variant="outlined" @click="fecharDialogo" class="px-8">
                    <v-icon start>mdi-close</v-icon>
                    Fechar 
                </v-btn>

            </v-card-actions>

            <!-- ✅ BOTÃO DE CANCELAR -->
            <v-card-actions class="pa-6 pt-0" v-else>
                <v-spacer></v-spacer>
                <v-btn color="error" variant="outlined" @click="cancelarAnalise" :disabled="progressoPercentual > 90">
                    <v-icon start>mdi-close</v-icon>
                    Cancelar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- ✅ DIALOG DO RESUMO GERAL DAS ORDENS -->
    <v-dialog v-model="dialogResumo" fullscreen persistent>
        <v-card v-if="ordensConsolidadas && ordensConsolidadas.length > 0" class="mt-4">
            <!-- ✅ HEADER ATUALIZADO -->
            <v-card-title class="bg-primary text-white d-flex align-center">
                <v-icon class="mr-2">mdi-table</v-icon>
                Status das Ordens - {{ setorSelecionado?.nome || 'Análise Geral' }}

                <v-chip v-if="tempoExecucao" color="white" variant="outlined" size="small" class="ml-2">
                    <v-icon start size="small">mdi-clock-outline</v-icon>
                    Tempo: {{ tempoExecucao }}
                </v-chip>

                <v-spacer></v-spacer>

                <!-- ✅ RESUMO RÁPIDO ATUALIZADO -->
                <v-chip color="white" size="small" class="mr-2" dark>
                    {{ ordensConcluidasCount }} Concluídas
                </v-chip>
                <v-chip color="white" size="small" class="mr-2" dark>
                    {{ ordensEmAndamentoCount }} Em Andamento
                </v-chip>
                <v-chip color="white" size="small" class="mr-2" dark>
                    {{ ordensPendentesCount }} Pendentes
                </v-chip>
                <!-- ✅ NOVO CHIP PARA PENDENTE DE AUTORIZAÇÃO -->
                <v-chip color="white" size="small" class="mr-2" dark>
                    {{ ordensPendenteAutorizacaoCount }} Pend. Autorização
                </v-chip>
                <!-- ✅ CHIP PARA SEM MOVIMENTAÇÃO -->
                <v-chip color="white" size="small" class="mr-2" dark>
                    {{ ordensSemMovimentacaoCount }} Sem Movimentação
                </v-chip>

                <!-- ✅ BOTÃO PARA FECHAR -->
                <v-btn color="white" variant="elevated" @click="fecharDialogo" class="ml-4">
                    <v-icon start>mdi-close</v-icon>
                    Fechar
                </v-btn>
            </v-card-title>

            <!-- ✅ TABELA AGRUPADA POR ORDEM -->
            <v-data-table :headers="headersTabela" :items="ordensAgrupadasFiltradas" :items-per-page="15"
                class="elevation-1" item-key="ordemId" show-expand :search="buscaTabela"
                :sort-by="[{ key: 'percentualGeralOrdem', order: 'desc' }]">
                <!-- ✅ SLOT DO TOPO ATUALIZADO -->
                <template v-slot:top>
                    <v-card flat>
                        <v-card-title class="d-flex align-center justify-space-between">
                            <div class="d-flex align-center">
                                <v-icon color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                                <span class="text-h6">
                                    Ordens Agrupadas
                                    <v-chip size="small" color="primary" variant="outlined" class="ml-2">
                                        {{ ordensAgrupadasFiltradas.length }} ordens
                                    </v-chip>
                                </span>
                            </div>

                            <!-- ✅ RESUMO RÁPIDO ATUALIZADO -->
                            <div class="d-flex align-center gap-2 flex-wrap">
                                <v-chip color="success" size="small">
                                    <v-icon start>mdi-check-circle</v-icon>
                                    {{ statusCounts.concluidas }} Concluídas
                                </v-chip>
                                <v-chip color="warning" size="small">
                                    <v-icon start>mdi-clock</v-icon>
                                    {{ statusCounts.emAndamento }} Em Andamento
                                </v-chip>
                                <v-chip color="error" size="small">
                                    <v-icon start>mdi-alert-circle</v-icon>
                                    {{ statusCounts.pendentes }} Pendentes
                                </v-chip>
                                <!-- ✅ NOVO CHIP PARA PENDENTE DE AUTORIZAÇÃO -->
                                <v-chip color="purple" size="small">
                                    <v-icon start>mdi-account-clock</v-icon>
                                    {{ statusCounts.pendenteAutorizacao }} Pend. Autorização
                                </v-chip>
                                <!-- ✅ CHIP PARA SEM MOVIMENTAÇÃO -->
                                <v-chip color="blue-grey" size="small">
                                    <v-icon start>mdi-pause-circle</v-icon>
                                    {{ statusCounts.semMovimentacao }} Sem Movimentação
                                </v-chip>
                            </div>
                        </v-card-title>

                        <!-- ✅ BARRA DE BUSCA E FILTROS -->
                        <v-card-text>
                            <v-row>
                                <!-- ✅ CAMPO DE BUSCA -->
                                <v-col cols="12" md="8">
                                    <v-text-field v-model="buscaTabela" label="Buscar por ID, OP, modelo ou parte..."
                                        prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" clearable
                                        hide-details></v-text-field>
                                </v-col>

                                <!-- ✅ FILTRO POR STATUS ATUALIZADO -->
                                <v-col cols="12" md="4">
                                    <v-select v-model="filtroStatus" :items="opcoesStatus" label="Filtrar por Status"
                                        variant="outlined" density="compact" clearable hide-details
                                        prepend-inner-icon="mdi-filter">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props">
                                                <template v-slot:prepend>
                                                    <v-icon :color="getStatusColor(item.value)" size="20">
                                                        {{ getStatusIcon(item.value) }}
                                                    </v-icon>
                                                </template>
                                            </v-list-item>
                                        </template>

                                        <template v-slot:selection="{ item }">
                                            <v-chip size="small" :color="getStatusColor(item.value)" variant="flat">
                                                <v-icon start size="16">{{ getStatusIcon(item.value) }}</v-icon>
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-select>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </template>

                <!-- ✅ TEMPLATES DOS SLOTS DA TABELA -->
                <template v-slot:item.ordemCompleta="{ item }">
                    <div class="d-flex align-center">
                        <v-avatar size="40" class="mr-3">
                            <v-icon :color="getStatusColor(item.statusGeralOrdem)" size="24">
                                {{ getStatusIcon(item.statusGeralOrdem) }}
                            </v-icon>
                        </v-avatar>
                        <div>
                            <div class="font-weight-bold text-subtitle-1">{{ item.ordemTexto }}</div>
                            <div class="text-caption text-primary d-flex align-center">
                                <v-icon size="14" class="mr-1">mdi-database</v-icon>
                                <strong>ID Cadastro: {{ item.ordemCadastro }}</strong>
                            </div>
                            <div class="text-caption text-grey d-flex align-center">
                                <v-icon size="12" class="mr-1">mdi-factory</v-icon>
                                {{ item.modelo }}
                                <v-divider vertical class="mx-2"></v-divider>
                                <v-icon size="12" class="mr-1">mdi-puzzle</v-icon>
                                {{ item.totalPartes }} parte(s)
                            </div>
                        </div>
                    </div>
                </template>

                <template v-slot:item.partes="{ item }">
                    <div>
                        <div class="d-flex flex-wrap gap-1 mb-2">
                            <v-chip v-for="parte in item.partes" :key="parte.itemId" size="small"
                                :color="getParteColor(parte)" variant="flat" class="font-weight-bold">
                                <v-icon start size="12">{{ getParteIcon(parte) }}</v-icon>
                                {{ parte.parte }} {{ parte.percentualConclusao }}%
                            </v-chip>
                        </div>
                        <div class="d-flex align-center justify-space-between">
                            <div class="text-caption text-grey">
                                <v-icon size="12" class="mr-1">mdi-check-circle</v-icon>
                                {{ item.totalPartesFinalizadas }}/{{ item.totalPartes }} finalizadas
                            </div>
                            <div class="text-caption text-primary font-weight-bold">
                                Média: {{ calcularMediaPercentual(item.partes) }}%
                            </div>
                        </div>
                    </div>
                </template>

                <template v-slot:item.producaoGeral="{ item }">
                    <div class="text-center">
                        <div class="font-weight-bold text-h6 mb-1">
                            <span :class="item.statusGeralOrdem === 'CONCLUIDA' ? 'text-success' : 'text-warning'">
                                {{ item.totalProduzidaGeral }}
                            </span>
                            <span class="text-grey">/</span>
                            <span>{{ item.totalPlanejadaGeral }}</span>
                        </div>
                        <v-progress-linear :model-value="item.percentualGeralOrdem" :color="getProgressColor(item)"
                            height="10" class="mt-1 mb-1" rounded striped></v-progress-linear>
                        <v-chip size="small" :color="getProgressColor(item)" variant="flat">
                            {{ item.percentualGeralOrdem }}%
                        </v-chip>
                        <!-- ✅ INDICADOR ESPECIAL PARA PENDENTE DE AUTORIZAÇÃO -->
                        <div v-if="item.statusGeralOrdem === 'PENDENTE_AUTORIZACAO_TERCEIRO'"
                            class="text-caption text-purple mt-1">
                            <v-icon size="12" class="mr-1">mdi-account-clock</v-icon>
                            Aguardando autorização
                        </div>
                    </div>
                </template>

                <template v-slot:item.totalSaldoDisponivelGeral="{ item }">
                    <div class="text-center">
                        <v-chip :color="getSaldoColor(item.totalSaldoDisponivelGeral)" size="large" variant="flat">
                            <v-icon start size="16">{{ getSaldoIcon(item.totalSaldoDisponivelGeral) }}</v-icon>
                            {{ item.totalSaldoDisponivelGeral }}
                        </v-chip>
                        <div class="text-caption text-grey mt-1">
                            {{ item.totalSaldoDisponivelGeral === 0 ? 'Finalizado' : 'Pendente' }}
                        </div>
                        <!-- ✅ MOSTRAR INFO DE TRÂNSITO PARA PENDENTE DE AUTORIZAÇÃO -->
                        <div v-if="item.statusGeralOrdem === 'PENDENTE_AUTORIZACAO_TERCEIRO' && item.totalTransitoGeral > 0"
                            class="text-caption text-purple mt-1">
                            <v-icon size="12" class="mr-1">mdi-truck</v-icon>
                            {{ item.totalTransitoGeral }} em trânsito
                        </div>
                    </div>
                </template>

                <template v-slot:item.statusGeralOrdem="{ item }">
                    <div class="text-center">
                        <v-chip :color="getStatusColor(item.statusGeralOrdem)" size="large" variant="flat"
                            class="font-weight-bold">
                            <v-icon start size="16">{{ getStatusIcon(item.statusGeralOrdem) }}</v-icon>
                            {{ getStatusTextoDetalhado(item.statusGeralOrdem, item.percentualGeralOrdem) }}
                        </v-chip>
                        <div class="text-caption text-grey mt-1">{{ item.dataVerificacao }}</div>
                    </div>
                </template>

                <!-- ✅ DETALHES EXPANDIDOS ATUALIZADO -->
                <template v-slot:expanded-row="{ item }">
                    <td :colspan="headersTabela.length">
                        <v-card flat class="ma-2">
                            <v-card-title class="d-flex align-center justify-space-between">
                                <div class="d-flex align-center">
                                    <v-icon color="primary" class="mr-2">mdi-puzzle</v-icon>
                                    <span class="text-h6">Partes da Ordem {{ item.ordemCadastro }}</span>
                                </div>
                                <v-chip size="small" color="info">
                                    <v-icon start size="12">mdi-layers</v-icon>
                                    {{ item.partes.length }} parte(s)
                                </v-chip>
                            </v-card-title>

                            <v-card-text>
                                <v-row>
                                    <v-col v-for="parte in item.partes" :key="parte.itemId" cols="12" md="6" lg="4">
                                        <v-card :color="getParteColor(parte)" variant="tonal" class="mb-2">
                                            <v-card-title class="d-flex align-center justify-space-between">
                                                <div class="d-flex align-center">
                                                    <v-icon class="mr-2">mdi-puzzle</v-icon>
                                                    <span class="text-h6">{{ parte.parte }}</span>
                                                    <v-chip size="small" color="primary" variant="outlined"
                                                        class="ml-2">
                                                        {{ parte.percentualConclusao }}%
                                                    </v-chip>
                                                </div>
                                                <v-chip size="small" :color="getParteColor(parte)" variant="flat">
                                                    {{ parte.statusTexto }}
                                                </v-chip>
                                            </v-card-title>

                                            <v-card-text>
                                                <div class="mb-3">
                                                    <div class="text-caption text-grey mb-1">Setor Atual:</div>
                                                    <v-chip size="small" color="blue-grey" variant="outlined">
                                                        <v-icon start size="12">mdi-factory</v-icon>
                                                        {{ getNomeSetorCompleto(parte) }}
                                                    </v-chip>
                                                </div>

                                                <div class="mb-3">
                                                    <div class="text-caption text-grey mb-1">Produção:</div>
                                                    <div class="font-weight-bold">
                                                        {{ parte.totalProduzida }}/{{ parte.totalPlanejada }} ({{
                                                            parte.percentualConclusao }}%)
                                                    </div>
                                                    <v-progress-linear :model-value="parte.percentualConclusao"
                                                        :color="parte.isConcluida ? 'success' : 'warning'" height="6"
                                                        class="mt-1" rounded></v-progress-linear>
                                                </div>

                                                <!-- ✅ CAMPO ATUALIZADO PARA PENDENTE DE AUTORIZAÇÃO -->
                                                <div class="mb-3"
                                                    v-if="parte.status === 'PENDENTE_AUTORIZACAO_TERCEIRO'">
                                                    <div class="text-caption text-grey mb-1">Status:</div>
                                                    <v-chip size="small" color="purple" variant="flat">
                                                        <v-icon start size="12">mdi-account-clock</v-icon>
                                                        Pendente de autorização terceiro
                                                    </v-chip>
                                                    <div v-if="parte.totalTransito > 0"
                                                        class="text-caption text-purple mt-1">
                                                        <v-icon size="12" class="mr-1">mdi-truck</v-icon>
                                                        {{ parte.totalTransito }} peças em trânsito
                                                    </div>
                                                </div>

                                                <!-- ✅ CAMPO PARA SEM MOVIMENTAÇÃO -->
                                                <div class="mb-3" v-else-if="parte.status === 'SEM_MOVIMENTACAO'">
                                                    <div class="text-caption text-grey mb-1">Movimentação:</div>
                                                    <v-chip size="small" color="blue-grey" variant="flat">
                                                        <v-icon start size="12">mdi-pause</v-icon>
                                                        Nenhuma movimentação detectada
                                                    </v-chip>
                                                </div>

                                                <div class="mb-3">
                                                    <div class="text-caption text-grey mb-1">Saldo Restante:</div>
                                                    <v-chip size="small"
                                                        :color="parte.totalSaldoDisponivel === 0 ? 'success' : 'warning'"
                                                        variant="flat">
                                                        <v-icon start size="12">
                                                            {{ parte.totalSaldoDisponivel === 0 ? 'mdi-check' :
                                                                'mdi-clock' }}
                                                        </v-icon>
                                                        {{ parte.totalSaldoDisponivel }} peças
                                                    </v-chip>
                                                </div>

                                                <div class="mb-3">
                                                    <div class="text-caption text-grey mb-1">IDs Status Cadastro:</div>
                                                    <div class="d-flex flex-wrap gap-1">
                                                        <v-chip v-for="id in parte.idsOrdemProducaoStatusCadastro"
                                                            :key="id" size="x-small" color="primary" variant="flat">
                                                            {{ id }}
                                                        </v-chip>
                                                    </div>
                                                </div>

                                                <!-- ✅ DETALHES POR TAMANHO -->
                                                <v-expansion-panels
                                                    v-if="parte.detalhesPartes && parte.detalhesPartes.length > 0">
                                                    <v-expansion-panel>
                                                        <v-expansion-panel-title>
                                                            <v-icon class="mr-2">mdi-resize</v-icon>
                                                            Ver Tamanhos ({{ parte.detalhesPartes.length }})
                                                        </v-expansion-panel-title>
                                                        <v-expansion-panel-text>
                                                            <v-table density="compact">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Tamanho</th>
                                                                        <th>Planejada</th>
                                                                        <th>Produzida</th>
                                                                        <th>Trânsito</th>
                                                                        <th>Saldo</th>
                                                                        <th>Status</th>
                                                                        <th>%</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr v-for="detalhe in parte.detalhesPartes"
                                                                        :key="detalhe.idOrdemProducaoStatusCadastro"
                                                                        :class="detalhe.statusGeral === 'finalizado' ? 'bg-green-lighten-5' : 'bg-orange-lighten-5'">
                                                                        <td>
                                                                            <v-chip size="x-small" color="blue-grey"
                                                                                variant="outlined">
                                                                                {{ detalhe.tamanho }}
                                                                            </v-chip>
                                                                        </td>
                                                                        <td class="font-weight-bold">{{
                                                                            detalhe.planejadaFinal }}</td>
                                                                        <td class="text-success font-weight-bold">{{
                                                                            detalhe.produzida }}</td>
                                                                        <td class="text-info font-weight-bold">{{
                                                                            detalhe.transito }}</td>
                                                                        <td>
                                                                            <v-chip size="x-small"
                                                                                :color="detalhe.saldoDisponivel === 0 ? 'success' : 'warning'">
                                                                                {{ detalhe.saldoDisponivel }}
                                                                            </v-chip>
                                                                        </td>
                                                                        <td>
                                                                            <v-chip size="x-small"
                                                                                :color="detalhe.statusGeral === 'finalizado' ? 'success' : 'warning'">
                                                                                {{ detalhe.statusGeral }}
                                                                            </v-chip>
                                                                        </td>
                                                                        <td>
                                                                            <div class="text-center">
                                                                                <v-progress-circular
                                                                                    :model-value="detalhe.planejadaFinal > 0 ? (detalhe.produzida / detalhe.planejadaFinal) * 100 : 0"
                                                                                    :color="detalhe.statusGeral === 'finalizado' ? 'success' : 'warning'"
                                                                                    size="24" width="3">
                                                                                    <span class="text-caption">
                                                                                        {{ detalhe.planejadaFinal > 0 ?
                                                                                            Math.round((detalhe.produzida /
                                                                                                detalhe.planejadaFinal) * 100) :
                                                                                            0 }}
                                                                                    </span>
                                                                                </v-progress-circular>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </v-table>
                                                        </v-expansion-panel-text>
                                                    </v-expansion-panel>
                                                </v-expansion-panels>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </td>
                </template>
            </v-data-table>
        </v-card>
    </v-dialog>
</template>

<script>
import ProgramacaoLocalizador from './ProgramacaoLocalizador.vue';

export default {
    name: 'AnaliseResumoSetor',
    components: {
        ProgramacaoLocalizador
    },
    emits: ['analise-concluida', 'analise-cancelada', 'progresso-atualizado'],

    data() {
        return {
            // ✅ NOVA FILA DE REPROCESSAMENTO
            filaReprocessamento: [],
            tentativasReprocessamento: 0,
            maxTentativasReprocessamento: 2,

            // ✅ DADOS RECEBIDOS DO PAI
            producaoFiltrada: [],
            setorSelecionado: null,
            producaoService: null,

            // ✅ CONTROLE DOS DIALOGS
            dialogProgresso: false,
            dialogResumo: false,

            // ✅ DADOS DO PROGRESSO
            progressoAtual: 0,
            progressoTotal: 0,
            progressoPercentual: 0,
            mensagemProgressoDetalhada: '',
            ordemAtualProcessando: null,
            tempoEstimado: null,
            inicioAnalise: null,
            analisesCancelada: false,

            // ✅ ESTATÍSTICAS ATUALIZADAS
            estatisticasTemp: {
                concluidas: 0,
                emAndamento: 0,
                pendentes: 0,
                semDados: 0,
                erros: 0,
                semMovimentacao: 0,
                pendenteAutorizacaoTerceiro: 0 // ✅ NOVA CATEGORIA
            },

            // ✅ LOG DE ATIVIDADES
            logAtividades: [],

            // ✅ DADOS DO RESUMO
            ordensConsolidadas: [],
            buscaTabela: '',
            filtroStatus: null, // ✅ NOVO FILTRO

            // ✅ HEADERS DA TABELA
            headersTabela: [
                { title: 'Ordem / Modelo', key: 'ordemCompleta', sortable: true, width: '350px' },
                { title: 'Partes', key: 'partes', sortable: false, width: '200px' },
                { title: 'Produção Geral', key: 'producaoGeral', sortable: true, align: 'center', width: '180px' },
                { title: 'Saldo Geral', key: 'totalSaldoDisponivelGeral', sortable: true, align: 'center', width: '120px' },
                { title: 'Status Geral', key: 'statusGeralOrdem', sortable: true, align: 'center', width: '150px' }
            ],

            // ✅ CONTROLE DE TEMPO DE EXECUÇÃO
            horarioInicial: null,
            horarioFinal: null,
            tempoExecucao: null,

            // ✅ MONITORAMENTO DE RATE LIMIT
            rateLimitDetectado: false,
            ultimoRateLimit: null,

            // ✅ OPÇÕES DE FILTRO ATUALIZADAS
            opcoesStatus: [
                { title: 'Concluídas', value: 'CONCLUIDA' },
                { title: 'Em Andamento', value: 'EM_ANDAMENTO' },
                { title: 'Pendentes', value: 'PENDENTE' },
                { title: 'Pendente de Autorização', value: 'PENDENTE_AUTORIZACAO_TERCEIRO' }, // ✅ NOVA OPÇÃO
                { title: 'Sem Movimentação', value: 'SEM_MOVIMENTACAO' },
                { title: 'Sem Dados', value: 'SEM_DADOS' },
                { title: 'Com Erro', value: 'ERRO' }
            ]
        }
    },

    computed: {
        ordensCompletas() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'CONCLUIDA')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: ordem.percentualConclusao || 100
                }));
        },

        ordensEmAndamento() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'EM_ANDAMENTO')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: ordem.percentualConclusao || 0
                }));
        },

        ordensPendentes() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'PENDENTE')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: ordem.percentualConclusao || 0
                }));
        },

        // ✅ NOVA COMPUTED PARA PENDENTE DE AUTORIZAÇÃO
        ordensPendenteAutorizacao() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'PENDENTE_AUTORIZACAO_TERCEIRO')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: 0,
                    totalTransito: ordem.totalTransito || 0,
                    totalSaidas: ordem.totalSaidas || 0
                }));
        },

        ordensSemMovimentacao() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'SEM_MOVIMENTACAO')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: 0
                }));
        },

        ordensSemDados() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'SEM_DADOS')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: 0
                }));
        },

        ordensComErro() {
            return this.ordensConsolidadas
                .filter(ordem => ordem.status === 'ERRO')
                .map(ordem => ({
                    id_ordem_cadastro: ordem.ordemCadastro,
                    id_ordem: ordem.idOrdemOriginal,
                    parte: ordem.parte,
                    setor: ordem.setorId,
                    percentual: 0
                }));
        },

        ordensAgrupadas() {
            return this.agruparOrdensPorId();
        },

        // ✅ COMPUTED FILTRADA
        ordensAgrupadasFiltradas() {
            let ordens = this.ordensAgrupadas;

            if (this.filtroStatus) {
                ordens = ordens.filter(ordem => ordem.statusGeralOrdem === this.filtroStatus);
            }

            return ordens;
        },

        ordensConcluidasCount() {
            return this.ordensConsolidadas.filter(o => o.isConcluida).length;
        },

        ordensEmAndamentoCount() {
            return this.ordensConsolidadas.filter(o => o.status === 'EM_ANDAMENTO').length;
        },

        ordensPendentesCount() {
            return this.ordensConsolidadas.filter(o => o.status === 'PENDENTE').length;
        },

        // ✅ NOVO CONTADOR PARA PENDENTE DE AUTORIZAÇÃO
        ordensPendenteAutorizacaoCount() {
            return this.ordensConsolidadas.filter(o => o.status === 'PENDENTE_AUTORIZACAO_TERCEIRO').length;
        },

        ordensSemMovimentacaoCount() {
            return this.ordensConsolidadas.filter(o => o.status === 'SEM_MOVIMENTACAO').length;
        },

        // ✅ STATUS COUNTS ATUALIZADO
        statusCounts() {
            const agrupadas = this.ordensAgrupadasFiltradas;
            return {
                concluidas: agrupadas.filter(o => o.statusGeralOrdem === 'CONCLUIDA').length,
                emAndamento: agrupadas.filter(o => o.statusGeralOrdem === 'EM_ANDAMENTO').length,
                pendentes: agrupadas.filter(o => o.statusGeralOrdem === 'PENDENTE').length,
                pendenteAutorizacao: agrupadas.filter(o => o.statusGeralOrdem === 'PENDENTE_AUTORIZACAO_TERCEIRO').length,
                semMovimentacao: agrupadas.filter(o => o.statusGeralOrdem === 'SEM_MOVIMENTACAO').length
            };
        }
    },

    methods: {
        fecharDialogo() {
            this.dialogProgresso = false;
            this.dialogResumo = false;
        },

        async executarAnalise(dados) {
            try {
                this.resetarEstado();
                this.horarioInicial = new Date();
                this.inicioAnalise = Date.now();
                this.dialogProgresso = true;

                this.producaoFiltrada = dados.producaoFiltrada;
                this.setorSelecionado = dados.setorSelecionado;
                this.producaoService = dados.producaoService;

                this.progressoTotal = this.producaoFiltrada.length;
                this.progressoAtual = 0;
                this.progressoPercentual = 0;

                this.adicionarLog('info', `Iniciando análise de ${this.progressoTotal} `);

                await this.getOrdensConcluidasComProgresso();
            } catch (error) {
                console.error('❌ Erro geral:', error);
                this.adicionarLog('erro', `Erro geral na análise: ${error.message}`);
                this.progressoPercentual = 100;
                this.mensagemProgressoDetalhada = 'Erro durante a análise';
            }
        },

        async getOrdensConcluidasComProgresso() {
            const setorSelecionado = this.setorSelecionado.id;
            this.ordensConsolidadas = [];
            this.analisesCancelada = false;

            try {
                let LOTE_SIZE = 150;
                let DELAY_ENTRE_LOTES = 500;
                const MAX_TENTATIVAS = 1;
                let DELAY_APOS_429 = 2000;
                let LOTES_SEM_ERRO = 0; // Contador para aumentar gradualmente
                let MODO_SEGURANCA = false; // Flag para mostrar mensagem fixa

                for (let i = 0; i < this.producaoFiltrada.length; i += LOTE_SIZE) {
                    if (this.analisesCancelada) {
                        this.adicionarLog('erro', 'Análise cancelada pelo usuário');
                        break;
                    }

                    const lote = this.producaoFiltrada.slice(i, i + LOTE_SIZE);

                    const promisesLote = lote.map(async (item, indexNoLote) => {
                        const indexGlobal = i + indexNoLote;

                        this.progressoAtual = indexGlobal + 1;
                        this.progressoPercentual = Math.round((this.progressoAtual / this.progressoTotal) * 100);

                        this.ordemAtualProcessando = {
                            ordemId: item.ordem.id,
                            ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo.descricao}`,
                            parte: item.parte
                        };

                        this.mensagemProgressoDetalhada = `Analisando ${item.parte} da OP ${item.ordem.id_ordem}`;
                        this.calcularTempoEstimado();

                        this.$emit('progresso-atualizado', {
                            atual: this.progressoAtual,
                            total: this.progressoTotal,
                            percentual: this.progressoPercentual,
                            ordem: this.ordemAtualProcessando
                        });

                        const executarComRetry = async (tentativa = 1) => {
                            try {
                                if (tentativa > 1) {
                                    const delayAntes = Math.min(1000 * Math.pow(2, tentativa - 2), 5000);
                          //          this.adicionarLog('info', `OP ${item.ordem.id_ordem} - ${item.parte}: Aguardando ${delayAntes / 1000}s antes da tentativa ${tentativa}...`);
                                    await new Promise(resolve => setTimeout(resolve, delayAntes));
                                }

                                if (tentativa > 1) {
                                    this.adicionarLog('info', `OP ${item.ordem.id_ordem} - ${item.parte}: Tentativa ${tentativa}/${MAX_TENTATIVAS}`);
                                }

                                const controller = new AbortController();
                                const timeoutId = setTimeout(() => controller.abort(), 30000);

                                const response = await this.producaoService.saldoEtapa(
                                    item.ordem.id,
                                    item.permitido_status,
                                    { signal: controller.signal }
                                );

                                clearTimeout(timeoutId);

                                if (tentativa > 1) {
                                    this.adicionarLog('sucesso', `OP ${item.ordem.id_ordem} - ${item.parte}: Sucesso na tentativa ${tentativa}`);
                                }

                                return response;

                            } catch (error) {
                                const isRateLimit = (
                                    error.response?.status === 429 ||
                                    error.status === 429 ||
                                    error.message?.includes('429') ||
                                    error.message?.toLowerCase().includes('too many requests') ||
                                    error.message?.toLowerCase().includes('rate limit')
                                );

                                const isNetworkError = (
                                    error.name === 'AbortError' ||
                                    error.code === 'NETWORK_ERROR' ||
                                    error.message?.toLowerCase().includes('network') ||
                                    error.message?.toLowerCase().includes('timeout')
                                );

                                const isServerError = (
                                    error.response?.status >= 500 ||
                                    error.status >= 500
                                );

                                if (isRateLimit) {
                                    this.monitorarRateLimit();
                                }

                                if (isRateLimit) {
                                    // Sistema adaptativo para erro 429 - SEM LOGS EXTRAS
                                    if (isRateLimit) {
                                        LOTE_SIZE = Math.max(Math.floor(LOTE_SIZE / 2), 20);
                                        DELAY_ENTRE_LOTES = Math.min(DELAY_ENTRE_LOTES * 2, 3000);
                                    } LOTES_SEM_ERRO = 0; // Reset contador

                                    if (!MODO_SEGURANCA) {
                                        MODO_SEGURANCA = true;
                                        // APENAS UMA MENSAGEM NO LOG
                                        this.adicionarLog('warning', '🛡️ MODO SEGURANÇA: Processamento reduzido para proteger servidor');
                                    }

                                    // Atualizar mensagem fixa na tela
                                    this.mensagemProgressoDetalhada = '🛡️ Modo Segurança: Processamento reduzido para proteger servidor';

                                    await new Promise(resolve => setTimeout(resolve, DELAY_APOS_429));

                                    // Retornar sucesso vazio para não gerar mais logs
                                    return {
                                        data: { saldos: [] },
                                        success: true // Marcar como sucesso para evitar logs de erro
                                    };
                                }

                                if ((isNetworkError || isServerError) && tentativa < MAX_TENTATIVAS) {
                                    let delayRetry = isNetworkError ? 2000 : 1500;
                                    this.adicionarLog('warning', `OP ${item.ordem.id_ordem} - ${item.parte}: ${isNetworkError ? 'Erro de rede' : 'Erro do servidor'} - Aguardando ${delayRetry / 1000}s...`);
                                    await new Promise(resolve => setTimeout(resolve, delayRetry));
                                    return await executarComRetry(tentativa + 1);
                                } else {
                                    if (isRateLimit || isNetworkError || isServerError) {
                                        this.filaReprocessamento.push(item);
                                    //    this.adicionarLog('warning', `📋 OP ${item.ordem.id_ordem} - ${item.parte}: Adicionado à fila de reprocessamento`);

                                        return {
                                            data: { saldos: [] },
                                            aguardandoReprocessamento: true,
                                            errorMessage: error.message,
                                            errorType: isRateLimit ? 'RATE_LIMIT' : isNetworkError ? 'NETWORK' : 'SERVER'
                                        };
                                    } else {
                                        this.adicionarLog('erro', `OP ${item.ordem.id_ordem} - ${item.parte}: Erro: ${error.message} - Continuando...`);

                                        return {
                                            data: { saldos: [] },
                                            error: true,
                                            errorMessage: error.message,
                                            errorType: 'OTHER'
                                        };
                                    }
                                }
                            }
                        };

                        try {
                            const response = await executarComRetry();

                            if (response.aguardandoReprocessamento) {
                                return {
                                    ordemId: item.ordem.id,
                                    ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo?.descricao || 'N/A'}`,
                                    parte: item.parte,
                                    itemId: item.id,
                                    setorId: setorSelecionado,
                                    status: 'AGUARDANDO_REPROCESSAMENTO',
                                    isConcluida: false,
                                    statusTexto: 'Aguardando reprocessamento',
                                    erro: response.errorMessage,
                                    dataVerificacao: new Date().toLocaleString('pt-BR')
                                };
                            }

                            if (response.error) {
                                this.estatisticasTemp.erros++;

                                return {
                                    ordemId: item.ordem.id,
                                    ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo?.descricao || 'N/A'}`,
                                    parte: item.parte,
                                    itemId: item.id,
                                    setorId: setorSelecionado,
                                    status: 'ERRO',
                                    isConcluida: false,
                                    statusTexto: `Erro: ${response.errorType}`,
                                    erro: response.errorMessage,
                                    dataVerificacao: new Date().toLocaleString('pt-BR')
                                };
                            }

                            let totalPlanejada = 0;
                            let totalProduzida = 0;
                            let totalSaldoDisponivel = 0;
                            let totalTransito = 0; // ✅ NOVO CAMPO
                            let detalhesPartes = [];

                            if (response?.data?.saldos && Array.isArray(response.data.saldos)) {
                                const saldosDaParte = response.data.saldos.filter(saldo =>
                                    saldo.parte && saldo.parte.toUpperCase() === item.parte.toUpperCase()
                                );

                                saldosDaParte.forEach(saldo => {
                                    const planejadaFinal = parseInt(saldo.planejada_final) || 0;
                                    const produzida = parseInt(saldo.produzida) || 0;
                                    const saldoDisponivel = parseInt(saldo.saldo_disponivel_produzir) || 0;
                                    const transito = parseInt(saldo.transito) || 0; // ✅ CAPTURAR TRÂNSITO

                                    totalPlanejada += planejadaFinal;
                                    totalProduzida += produzida;
                                    totalSaldoDisponivel += saldoDisponivel;
                                    totalTransito += transito; // ✅ SOMAR TRÂNSITO

                                    detalhesPartes.push({
                                        idOrdemProducaoStatusCadastro: saldo.id_ordem_producao_status_cadastro,
                                        tamanho: saldo.tamanho,
                                        planejadaInicial: parseInt(saldo.planejada_inicial) || 0,
                                        planejadaFinal: planejadaFinal,
                                        produzida: produzida,
                                        transito: transito, // ✅ INCLUIR TRÂNSITO
                                        saldoDisponivel: saldoDisponivel,
                                        statusGeral: saldo.status_geral,
                                        comErro: parseInt(saldo.setor_finalizado_com_erro) || 0,
                                        entradas: parseInt(saldo.entradas) || 0,
                                        saidas: parseInt(saldo.saidas) || 0,
                                        nomeSetor: saldo.nome_setor
                                    });
                                });
                            }

                            // ✅ PASSAR TRÂNSITO PARA DETERMINAÇÃO DE STATUS
                            const statusInfo = this.determinarStatusCorreto(
                                totalProduzida,
                                totalPlanejada,
                                totalSaldoDisponivel,
                                detalhesPartes,
                                totalTransito // ✅ NOVO PARÂMETRO
                            );

                            this.atualizarEstatisticas(statusInfo.status);
                          //  this.adicionarLog('sucesso', `OP ${item.ordem.id_ordem} - ${item.parte}: ${statusInfo.status} (${statusInfo.percentualConclusao}%) - Produzido: ${totalProduzida}/${totalPlanejada}${totalTransito > 0 ? ` - Trânsito: ${totalTransito}` : ''}`);

                            const resultado = {
                                ordemId: item.ordem.id,
                                ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo.descricao}`,
                                ordemCadastro: item.ordem.id,
                                idOrdemOriginal: item.ordem.id_ordem,
                                parte: item.parte,
                                itemId: item.id,
                                idsOrdemProducaoStatusCadastro: detalhesPartes.map(d => d.idOrdemProducaoStatusCadastro),
                                setorId: setorSelecionado,
                                setorNome: this.setorSelecionado.nome,
                                totalPlanejada,
                                totalProduzida,
                                totalSaldoDisponivel,
                                totalTransito, // ✅ NOVO CAMPO
                                totalItens: detalhesPartes.length,
                                percentualConclusao: statusInfo.percentualConclusao,
                                status: statusInfo.status,
                                isConcluida: statusInfo.isConcluida,
                                statusTexto: this.getStatusTextoDetalhado(statusInfo.status, statusInfo.percentualConclusao),
                                detalhesPartes,
                                modelo: item.ordem.modelo.descricao,
                                quantidade: item.ordem.quantidade,
                                dataVerificacao: new Date().toLocaleString('pt-BR'),
                                temMovimentacao: statusInfo.temMovimentacao,
                                totalEntradas: detalhesPartes.reduce((sum, d) => sum + d.entradas, 0),
                                totalSaidas: detalhesPartes.reduce((sum, d) => sum + d.saidas, 0)
                            };

                            return resultado;

                        } catch (error) {
                            console.error(`❌ Erro definitivo ao processar ordem ${item.ordem.id} - Parte ${item.parte}:`, error);

                            this.estatisticasTemp.erros++;

                            return {
                                ordemId: item.ordem.id,
                                ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo?.descricao || 'N/A'}`,
                                parte: item.parte,
                                itemId: item.id,
                                setorId: setorSelecionado,
                                status: 'ERRO',
                                isConcluida: false,
                                statusTexto: 'Erro na consulta',
                                erro: error.message,
                                dataVerificacao: new Date().toLocaleString('pt-BR')
                            };
                        }
                    });

                    try {
                        const resultadosLote = await Promise.all(promisesLote);

                        // Sistema de recuperação em etapas: 5 → 10 → 15 → 20
                        if (MODO_SEGURANCA) {
                            LOTES_SEM_ERRO++;

                            if (LOTES_SEM_ERRO >= 1) {
                                // A cada lote sem erro, subir para próximo nível
                                if (LOTE_SIZE === 5) {
                                    LOTE_SIZE = 20;
                                    DELAY_ENTRE_LOTES = 2000;
                                    this.mensagemProgressoDetalhada = '🔄 Recuperando: Lote=10';
                                } else if (LOTE_SIZE === 20) {
                                    LOTE_SIZE = 35;
                                    DELAY_ENTRE_LOTES = 1500;
                                    this.mensagemProgressoDetalhada = '🔄 Recuperando: Lote=15';
                                } else if (LOTE_SIZE === 35) {
                                    LOTE_SIZE = 50;
                                    DELAY_ENTRE_LOTES = 1000;
                                    MODO_SEGURANCA = false;
                                    this.adicionarLog('sucesso', '✅ VELOCIDADE RESTAURADA');
                                    this.mensagemProgressoDetalhada = `Analisando dados do setor ${this.setorSelecionado.nome}`;
                                }

                                LOTES_SEM_ERRO = 0; // Reset para próximo aumento
                            }
                        }

                        resultadosLote.forEach(resultado => {
                            if (resultado && resultado.status !== 'AGUARDANDO_REPROCESSAMENTO') {
                                this.ordensConsolidadas.push(resultado);
                            }
                        });

                    } catch (error) {
                        console.error('❌ Erro no lote:', error);
                    }

                    // Sistema antigo removido - usando novo sistema adaptativo

                    if (i + LOTE_SIZE < this.producaoFiltrada.length) {
                        await new Promise(resolve => setTimeout(resolve, DELAY_ENTRE_LOTES));
                    }
                }

                await this.processarFilaReprocessamento();

                this.progressoPercentual = 100;
                this.horarioFinal = new Date();
                const tempoDecorrido = (this.horarioFinal - this.horarioInicial) / 1000;
                this.tempoExecucao = this.formatarTempoExecucao(tempoDecorrido);
                this.mensagemProgressoDetalhada = `Análise concluída com sucesso em ${this.tempoExecucao}!`;
                this.ordemAtualProcessando = null;

                const concluidas = this.ordensConsolidadas.filter(o => o.isConcluida).length;
                const emAndamento = this.ordensConsolidadas.filter(o => o.status === 'EM_ANDAMENTO').length;
                const pendentes = this.ordensConsolidadas.filter(o => o.status === 'PENDENTE').length;
                const pendenteAutorizacao = this.ordensConsolidadas.filter(o => o.status === 'PENDENTE_AUTORIZACAO_TERCEIRO').length; // ✅ NOVO
                const semDados = this.ordensConsolidadas.filter(o => o.status === 'SEM_DADOS').length;
                const erros = this.ordensConsolidadas.filter(o => o.status === 'ERRO').length;
                const semMovimentacao = this.ordensConsolidadas.filter(o => o.status === 'SEM_MOVIMENTACAO').length;

                this.adicionarLog('sucesso', `Análise finalizada: ${concluidas} concluídas, ${emAndamento} em andamento, ${pendentes} pendentes, ${pendenteAutorizacao} pend. autorização, ${semMovimentacao} sem movimentação, ${semDados} sem dados, ${erros} erros`);

                this.$emit('analise-concluida', {
                    ordensConsolidadas: this.ordensConsolidadas,
                    estatisticas: this.estatisticasTemp,
                    setor: this.setorSelecionado,
                    completas: this.ordensCompletas,
                    emAndamento: this.ordensEmAndamento,
                    pendentes: this.ordensPendentes,
                    pendenteAutorizacao: this.ordensPendenteAutorizacao, // ✅ NOVO
                    semMovimentacao: this.ordensSemMovimentacao
                });

            } catch (error) {
                console.error('❌ Erro geral:', error);
                this.adicionarLog('erro', `Erro geral na análise: ${error.message}`);
                this.progressoPercentual = 100;
                this.mensagemProgressoDetalhada = 'Erro durante a análise';
            }
        },

        async processarFilaReprocessamento() {
            if (this.filaReprocessamento.length === 0) return;

            this.adicionarLog('info', `🔄 Reprocessando ${this.filaReprocessamento.length} itens que falharam...`);

            const LOTE_SIZE_REPROCESS = 1;
            const DELAY_REPROCESS = 3000;

            while (this.filaReprocessamento.length > 0 && this.tentativasReprocessamento < this.maxTentativasReprocessamento) {
                this.tentativasReprocessamento++;
                this.adicionarLog('info', `🔄 Tentativa de reprocessamento ${this.tentativasReprocessamento}/${this.maxTentativasReprocessamento}`);

                const itensParaReprocessar = [...this.filaReprocessamento];
                this.filaReprocessamento = [];

                for (const item of itensParaReprocessar) {
                    if (this.analisesCancelada) break;

                    this.mensagemProgressoDetalhada = `Reprocessando ${item.parte} da OP ${item.ordem.id_ordem}`;

                    try {
                        const resultado = await this.processarItemIndividual(item);

                        if (resultado && !resultado.error) {
                            this.ordensConsolidadas.push(resultado);
                        //    this.adicionarLog('sucesso', `✅ Reprocessado com sucesso: OP ${item.ordem.id_ordem} - ${item.parte}`);
                        } else {
                            this.filaReprocessamento.push(item);
                        }

                    } catch (error) {
                        this.filaReprocessamento.push(item);
                        this.adicionarLog('warning', `⚠️ Erro no reprocessamento: OP ${item.ordem.id_ordem} - ${item.parte}`);
                    }

                    await new Promise(resolve => setTimeout(resolve, DELAY_REPROCESS));
                }
            }

            if (this.filaReprocessamento.length > 0) {
                this.adicionarLog('warning', `⚠️ ${this.filaReprocessamento.length} itens não puderam ser reprocessados após ${this.maxTentativasReprocessamento} tentativas`);

                this.filaReprocessamento.forEach(item => {
                    this.ordensConsolidadas.push({
                        ordemId: item.ordem.id,
                        ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo?.descricao || 'N/A'}`,
                        parte: item.parte,
                        itemId: item.id,
                        setorId: this.setorSelecionado.id,
                        status: 'ERRO',
                        isConcluida: false,
                        statusTexto: 'Erro persistente após reprocessamento',
                        erro: 'Rate limit persistente',
                        dataVerificacao: new Date().toLocaleString('pt-BR')
                    });
                });
            } else {
                this.adicionarLog('sucesso', '✅ Todos os itens foram reprocessados com sucesso!');
            }
        },

        async processarItemIndividual(item) {
            const MAX_TENTATIVAS_REPROCESS = 2;

            for (let tentativa = 1; tentativa <= MAX_TENTATIVAS_REPROCESS; tentativa++) {
                try {
                    const delayAntes = 2000 * tentativa;
                    if (tentativa > 1) {
                        await new Promise(resolve => setTimeout(resolve, delayAntes));
                    }

                    const response = await this.producaoService.saldoEtapa(
                        item.ordem.id,
                        item.permitido_status
                    );

                    return this.processarResponse(response, item);

                } catch (error) {
                    if (tentativa === MAX_TENTATIVAS_REPROCESS) {
                        return { error: true, errorMessage: error.message };
                    }
                }
            }
        },

        processarResponse(response, item) {
            let totalPlanejada = 0;
            let totalProduzida = 0;
            let totalSaldoDisponivel = 0;
            let totalTransito = 0; // ✅ NOVO
            let detalhesPartes = [];

            if (response?.data?.saldos && Array.isArray(response.data.saldos)) {
                const saldosDaParte = response.data.saldos.filter(saldo =>
                    saldo.parte && saldo.parte.toUpperCase() === item.parte.toUpperCase()
                );

                saldosDaParte.forEach(saldo => {
                    const planejadaFinal = parseInt(saldo.planejada_final) || 0;
                    const produzida = parseInt(saldo.produzida) || 0;
                    const saldoDisponivel = parseInt(saldo.saldo_disponivel_produzir) || 0;
                    const transito = parseInt(saldo.transito) || 0; // ✅ CAPTURAR TRÂNSITO

                    totalPlanejada += planejadaFinal;
                    totalProduzida += produzida;
                    totalSaldoDisponivel += saldoDisponivel;
                    totalTransito += transito; // ✅ SOMAR TRÂNSITO

                    detalhesPartes.push({
                        idOrdemProducaoStatusCadastro: saldo.id_ordem_producao_status_cadastro,
                        tamanho: saldo.tamanho,
                        planejadaInicial: parseInt(saldo.planejada_inicial) || 0,
                        planejadaFinal: planejadaFinal,
                        produzida: produzida,
                        transito: transito, // ✅ INCLUIR TRÂNSITO
                        saldoDisponivel: saldoDisponivel,
                        statusGeral: saldo.status_geral,
                        comErro: parseInt(saldo.setor_finalizado_com_erro) || 0,
                        entradas: parseInt(saldo.entradas) || 0,
                        saidas: parseInt(saldo.saidas) || 0,
                        nomeSetor: saldo.nome_setor
                    });
                });
            }

            // ✅ PASSAR TRÂNSITO PARA DETERMINAÇÃO DE STATUS
            const statusInfo = this.determinarStatusCorreto(
                totalProduzida,
                totalPlanejada,
                totalSaldoDisponivel,
                detalhesPartes,
                totalTransito // ✅ NOVO PARÂMETRO
            );

            this.atualizarEstatisticas(statusInfo.status);

            return {
                ordemId: item.ordem.id,
                ordemTexto: `OP ${item.ordem.id_ordem} - ${item.ordem.modelo.descricao}`,
                ordemCadastro: item.ordem.id,
                idOrdemOriginal: item.ordem.id_ordem,
                parte: item.parte,
                itemId: item.id,
                idsOrdemProducaoStatusCadastro: detalhesPartes.map(d => d.idOrdemProducaoStatusCadastro),
                setorId: this.setorSelecionado.id,
                setorNome: this.setorSelecionado.nome,
                totalPlanejada,
                totalProduzida,
                totalSaldoDisponivel,
                totalTransito, // ✅ NOVO CAMPO
                totalItens: detalhesPartes.length,
                percentualConclusao: statusInfo.percentualConclusao,
                status: statusInfo.status,
                isConcluida: statusInfo.isConcluida,
                statusTexto: this.getStatusTextoDetalhado(statusInfo.status, statusInfo.percentualConclusao),
                detalhesPartes,
                modelo: item.ordem.modelo.descricao,
                quantidade: item.ordem.quantidade,
                dataVerificacao: new Date().toLocaleString('pt-BR'),
                temMovimentacao: statusInfo.temMovimentacao,
                totalEntradas: detalhesPartes.reduce((sum, d) => sum + d.entradas, 0),
                totalSaidas: detalhesPartes.reduce((sum, d) => sum + d.saidas, 0)
            };
        },

        monitorarRateLimit() {
            const agora = Date.now();
            if (this.ultimoRateLimit && (agora - this.ultimoRateLimit) < 60000) {
                this.rateLimitDetectado = true;
                this.adicionarLog('info', '🔄 Sistema adaptativo ativado - Reduzindo velocidade para evitar novos rate limits');
            }
            this.ultimoRateLimit = agora;
        },

        calcularPercentualPreciso(produzida, planejada) {
            if (!planejada || planejada === 0) return 0;

            const percentualReal = (produzida / planejada) * 100;

            if (produzida >= planejada) {
                return 100;
            }

            if (percentualReal >= 99.5) {
                return 99;
            }

            return Math.floor(percentualReal);
        },

        // ✅ MÉTODO ATUALIZADO PARA DETECTAR PENDENTE DE AUTORIZAÇÃO
        determinarStatusCorreto(totalProduzida, totalPlanejada, totalSaldoDisponivel, detalhesPartes, totalTransito = 0) {
            if (!detalhesPartes || detalhesPartes.length === 0) {
                return {
                    status: 'SEM_DADOS',
                    isConcluida: false,
                    percentualConclusao: 0,
                    temMovimentacao: false
                };
            }

            // ✅ VERIFICAR MOVIMENTAÇÃO
            const totalEntradas = detalhesPartes.reduce((sum, d) => sum + d.entradas, 0);
            const totalSaidas = detalhesPartes.reduce((sum, d) => sum + d.saidas, 0);
            const temMovimentacao = totalEntradas > 0 || totalSaidas > 0 || totalProduzida > 0;

            // ✅ LÓGICA SIMPLIFICADA: SEM LIMITE DE PERCENTUAL
            const produzidaZero = totalProduzida === 0;
            const temPecasEmTransito = totalTransito > 0;
            const temSaidas = totalSaidas > 0;

            // ✅ CRITÉRIO SIMPLES: Apenas verificar se tem trânsito OU saídas com produção zero
            const possivelPendenteAutorizacao = (
                produzidaZero &&
                (temPecasEmTransito || temSaidas)
            );

            // ✅ LOG PARA DEBUG (opcional - remover depois)
            // console.log('🔍 Verificando PENDENTE_AUTORIZACAO_TERCEIRO:', {
            //     produzidaZero,
            //     temPecasEmTransito,
            //     temSaidas,
            //     totalTransito,
            //     totalSaidas,
            //     possivelPendenteAutorizacao
            // });

            if (possivelPendenteAutorizacao) {
                // console.log('🟣 STATUS DETECTADO: PENDENTE_AUTORIZACAO_TERCEIRO');
                return {
                    status: 'PENDENTE_AUTORIZACAO_TERCEIRO',
                    isConcluida: false,
                    percentualConclusao: 0,
                    temMovimentacao: true,
                    totalTransito: totalTransito,
                    totalSaidas: totalSaidas
                };
            }

            // ✅ RESTO DA LÓGICA EXISTENTE
            const todosFinalizados = detalhesPartes.every(d => d.statusGeral === 'finalizado');
            const percentualConclusao = this.calcularPercentualPreciso(totalProduzida, totalPlanejada);

            // Se não tem movimentação e não produziu nada
            if (!temMovimentacao && totalProduzida === 0) {
                return {
                    status: 'SEM_MOVIMENTACAO',
                    isConcluida: false,
                    percentualConclusao: 0,
                    temMovimentacao: false
                };
            }

            const realmenteConcluida = (
                totalProduzida >= totalPlanejada &&
                todosFinalizados &&
                totalSaldoDisponivel === 0
            );

            if (realmenteConcluida) {
                return {
                    status: 'CONCLUIDA',
                    isConcluida: true,
                    percentualConclusao: 100,
                    temMovimentacao: true
                };
            } else if (totalProduzida > 0) {
                return {
                    status: 'EM_ANDAMENTO',
                    isConcluida: false,
                    percentualConclusao: percentualConclusao,
                    temMovimentacao: true
                };
            } else {
                return {
                    status: 'PENDENTE',
                    isConcluida: false,
                    percentualConclusao: 0,
                    temMovimentacao: temMovimentacao
                };
            }
        },
        resetarEstado() {
            this.ordensConsolidadas = [];
            this.filaReprocessamento = [];
            this.tentativasReprocessamento = 0;
            this.progressoAtual = 0;
            this.progressoTotal = 0;
            this.progressoPercentual = 0;
            this.mensagemProgressoDetalhada = 'Iniciando análise...';
            this.ordemAtualProcessando = null;
            this.filtroStatus = null; // ✅ RESETAR FILTRO

            // ✅ ESTATÍSTICAS ATUALIZADAS
            this.estatisticasTemp = {
                concluidas: 0,
                emAndamento: 0,
                pendentes: 0,
                semDados: 0,
                erros: 0,
                semMovimentacao: 0,
                pendenteAutorizacaoTerceiro: 0 // ✅ RESETAR NOVA CATEGORIA
            };

            this.logAtividades = [];
            this.tempoEstimado = null;
            this.horarioInicial = null;
            this.horarioFinal = null;
            this.tempoExecucao = null;
            this.rateLimitDetectado = false;
            this.ultimoRateLimit = null;
        },

        formatarTempoExecucao(segundos) {
            if (segundos < 60) {
                return `${segundos.toFixed(1)} segundos`;
            } else {
                const minutos = Math.floor(segundos / 60);
                const segundosRestantes = Math.round(segundos % 60);
                return `${minutos} min e ${segundosRestantes} seg`;
            }
        },

        // ✅ MÉTODO ATUALIZADO PARA INCLUIR PENDENTE DE AUTORIZAÇÃO
        atualizarEstatisticas(status) {
            switch (status) {
                case 'CONCLUIDA':
                    this.estatisticasTemp.concluidas++;
                    break;
                case 'EM_ANDAMENTO':
                    this.estatisticasTemp.emAndamento++;
                    break;
                case 'PENDENTE':
                    this.estatisticasTemp.pendentes++;
                    break;
                case 'PENDENTE_AUTORIZACAO_TERCEIRO': // ✅ NOVO CASE
                    this.estatisticasTemp.pendenteAutorizacaoTerceiro++;
                    break;
                case 'SEM_DADOS':
                    this.estatisticasTemp.semDados++;
                    break;
                case 'SEM_MOVIMENTACAO':
                    this.estatisticasTemp.semMovimentacao++;
                    break;
                default:
                    this.estatisticasTemp.erros++;
            }
        },

        adicionarLog(tipo, mensagem) {
            const agora = new Date();
            this.logAtividades.push({
                tipo,
                mensagem,
                hora: agora.toLocaleTimeString('pt-BR')
            });

            if (this.logAtividades.length > 20) {
                this.logAtividades = this.logAtividades.slice(-20);
            }
        },

        calcularTempoEstimado() {
            if (this.progressoAtual > 2 && this.inicioAnalise) {
                const tempoDecorrido = Date.now() - this.inicioAnalise;
                const tempoMedioPorItem = tempoDecorrido / this.progressoAtual;
                const itensRestantes = this.progressoTotal - this.progressoAtual;
                const tempoEstimadoMs = itensRestantes * tempoMedioPorItem;

                const segundos = Math.round(tempoEstimadoMs / 1000);
                if (segundos < 60) {
                    this.tempoEstimado = `${segundos}s`;
                } else {
                    const minutos = Math.floor(segundos / 60);
                    const segundosRestantes = segundos % 60;
                    this.tempoEstimado = `${minutos}m ${segundosRestantes}s`;
                }
            }
        },

        abrirResumo() {
            this.dialogProgresso = false;
            this.dialogResumo = true;
        },

        fecharResumo() {
            this.dialogResumo = false;
        },

        cancelarAnalise() {
            this.analisesCancelada = true;
            this.dialogProgresso = false;
            this.adicionarLog('erro', 'Análise cancelada pelo usuário');
            this.$emit('analise-cancelada');
        },

        agruparOrdensPorId() {
            if (!this.ordensConsolidadas || this.ordensConsolidadas.length === 0) {
                return [];
            }

            const ordensAgrupadas = new Map();

            this.ordensConsolidadas.forEach(item => {
                const ordemId = item.ordemId;

                if (!ordensAgrupadas.has(ordemId)) {
                    ordensAgrupadas.set(ordemId, {
                        ordemId: ordemId,
                        ordemTexto: item.ordemTexto,
                        ordemCadastro: item.ordemCadastro,
                        idOrdemOriginal: item.idOrdemOriginal,
                        modelo: item.modelo,
                        quantidade: item.quantidade,
                        partes: [],
                        totalPlanejadaGeral: 0,
                        totalProduzidaGeral: 0,
                        totalSaldoDisponivelGeral: 0,
                        totalTransitoGeral: 0, // ✅ NOVO CAMPO
                        totalPartesFinalizadas: 0,
                        totalPartes: 0,
                        statusGeralOrdem: 'INDEFINIDO',
                        percentualGeralOrdem: 0,
                        dataVerificacao: item.dataVerificacao,
                        temMovimentacaoGeral: false,
                        totalEntradasGeral: 0,
                        totalSaidasGeral: 0
                    });
                }

                const ordem = ordensAgrupadas.get(ordemId);

                ordem.partes.push({
                    parte: item.parte,
                    itemId: item.itemId,
                    setorId: item.setorId,
                    setorNome: item.setorNome,
                    idsOrdemProducaoStatusCadastro: item.idsOrdemProducaoStatusCadastro,
                    totalPlanejada: item.totalPlanejada,
                    totalProduzida: item.totalProduzida,
                    totalSaldoDisponivel: item.totalSaldoDisponivel,
                    totalTransito: item.totalTransito || 0, // ✅ NOVO CAMPO
                    totalItens: item.totalItens,
                    percentualConclusao: item.percentualConclusao,
                    status: item.status,
                    isConcluida: item.isConcluida,
                    statusTexto: item.statusTexto,
                    detalhesPartes: item.detalhesPartes,
                    temMovimentacao: item.temMovimentacao,
                    totalEntradas: item.totalEntradas || 0,
                    totalSaidas: item.totalSaidas || 0
                });

                ordem.totalPlanejadaGeral += item.totalPlanejada;
                ordem.totalProduzidaGeral += item.totalProduzida;
                ordem.totalSaldoDisponivelGeral += item.totalSaldoDisponivel;
                ordem.totalTransitoGeral += item.totalTransito || 0; // ✅ SOMAR TRÂNSITO
                ordem.totalPartes = ordem.partes.length;

                ordem.totalEntradasGeral += item.totalEntradas || 0;
                ordem.totalSaidasGeral += item.totalSaidas || 0;
                if (item.temMovimentacao) {
                    ordem.temMovimentacaoGeral = true;
                }

                if (item.isConcluida) {
                    ordem.totalPartesFinalizadas++;
                }
            });

            Array.from(ordensAgrupadas.values()).forEach(ordem => {
                const todasPartesCompletas = (
                    ordem.totalPartesFinalizadas === ordem.totalPartes &&
                    ordem.totalProduzidaGeral >= ordem.totalPlanejadaGeral &&
                    ordem.totalSaldoDisponivelGeral === 0
                );

                // ✅ VERIFICAR SE TODAS AS PARTES SÃO PENDENTE DE AUTORIZAÇÃO
                const todasPartesPendenteAutorizacao = ordem.partes.every(p => p.status === 'PENDENTE_AUTORIZACAO_TERCEIRO');

                // ✅ VERIFICAR SE TODAS AS PARTES SÃO SEM MOVIMENTAÇÃO
                const todasPartesSemMovimentacao = ordem.partes.every(p => p.status === 'SEM_MOVIMENTACAO');

                if (todasPartesPendenteAutorizacao) {
                    ordem.statusGeralOrdem = 'PENDENTE_AUTORIZACAO_TERCEIRO';
                    ordem.percentualGeralOrdem = 0;
                } else if (todasPartesSemMovimentacao) {
                    ordem.statusGeralOrdem = 'SEM_MOVIMENTACAO';
                    ordem.percentualGeralOrdem = 0;
                } else if (todasPartesCompletas) {
                    ordem.statusGeralOrdem = 'CONCLUIDA';
                    ordem.percentualGeralOrdem = 100;
                } else if (ordem.totalProduzidaGeral > 0) {
                    ordem.statusGeralOrdem = 'EM_ANDAMENTO';
                    ordem.percentualGeralOrdem = this.calcularPercentualPreciso(
                        ordem.totalProduzidaGeral,
                        ordem.totalPlanejadaGeral
                    );
                } else {
                    ordem.statusGeralOrdem = 'PENDENTE';
                    ordem.percentualGeralOrdem = 0;
                }
            });

            return Array.from(ordensAgrupadas.values()).sort((a, b) => {
                return parseInt(a.ordemId) - parseInt(b.ordemId);
            });
        },

        calcularMediaPercentual(partes) {
            if (!partes || partes.length === 0) return 0;

            const somaPercentuais = partes.reduce((soma, parte) => {
                return soma + (parte.percentualConclusao || 0);
            }, 0);

            return Math.round(somaPercentuais / partes.length);
        },

        getNomeSetorCompleto(parte) {
            if (parte.detalhesPartes && parte.detalhesPartes.length > 0) {
                const nomeSetor = parte.detalhesPartes[0].nomeSetor;
                if (nomeSetor) {
                    return nomeSetor;
                }
            }
            return `Setor ${parte.setorId}`;
        },

        getLogColor(tipo) {
            const colorMap = {
                'sucesso': 'success',
                'erro': 'error',
                'info': 'info',
                'warning': 'warning'
            };
            return colorMap[tipo] || 'info';
        },

        getLogIcon(tipo) {
            const iconMap = {
                'sucesso': 'mdi-check',
                'erro': 'mdi-alert',
                'info': 'mdi-information',
                'warning': 'mdi-alert-circle'
            };
            return iconMap[tipo] || 'mdi-information';
        },

        // ✅ MÉTODO ATUALIZADO PARA INCLUIR PENDENTE DE AUTORIZAÇÃO
        getStatusColor(status) {
            const colorMap = {
                'CONCLUIDA': 'success',
                'EM_ANDAMENTO': 'warning',
                'PENDENTE': 'error',
                'PENDENTE_AUTORIZACAO_TERCEIRO': 'purple', // ✅ NOVA COR
                'SEM_DADOS': 'grey',
                'ERRO': 'error',
                'INDEFINIDO': 'grey',
                'SEM_MOVIMENTACAO': 'blue-grey'
            };
            return colorMap[status] || 'grey';
        },

        // ✅ MÉTODO ATUALIZADO PARA INCLUIR PENDENTE DE AUTORIZAÇÃO
        getStatusIcon(status) {
            const iconMap = {
                'CONCLUIDA': 'mdi-check-circle',
                'EM_ANDAMENTO': 'mdi-clock',
                'PENDENTE': 'mdi-alert-circle',
                'PENDENTE_AUTORIZACAO_TERCEIRO': 'mdi-account-clock', // ✅ NOVO ÍCONE
                'SEM_DADOS': 'mdi-database-off',
                'ERRO': 'mdi-alert',
                'INDEFINIDO': 'mdi-help-circle',
                'SEM_MOVIMENTACAO': 'mdi-pause-circle'
            };
            return iconMap[status] || 'mdi-help-circle';
        },

        getParteColor(parte) {
            if (parte.isConcluida) return 'success';
            if (parte.status === 'PENDENTE_AUTORIZACAO_TERCEIRO') return 'purple'; // ✅ NOVA COR
            if (parte.status === 'SEM_MOVIMENTACAO') return 'blue-grey';
            if (parte.percentualConclusao > 70) return 'warning';
            return 'error';
        },

        getParteIcon(parte) {
            if (parte.isConcluida) return 'mdi-check';
            if (parte.status === 'PENDENTE_AUTORIZACAO_TERCEIRO') return 'mdi-account-clock'; // ✅ NOVO ÍCONE
            if (parte.status === 'SEM_MOVIMENTACAO') return 'mdi-pause';
            if (parte.percentualConclusao > 70) return 'mdi-clock';
            return 'mdi-alert';
        },

        getProgressColor(item) {
            if (item.statusGeralOrdem === 'CONCLUIDA') return 'success';
            if (item.statusGeralOrdem === 'PENDENTE_AUTORIZACAO_TERCEIRO') return 'purple'; // ✅ NOVA COR
            if (item.statusGeralOrdem === 'SEM_MOVIMENTACAO') return 'blue-grey';
            if (item.percentualGeralOrdem > 70) return 'warning';
            return 'error';
        },

        getSaldoColor(saldo) {
            if (saldo === 0) return 'success';
            if (saldo > 100) return 'error';
            return 'warning';
        },

        getSaldoIcon(saldo) {
            return saldo === 0 ? 'mdi-check-circle' : 'mdi-clock-alert';
        },

        // ✅ MÉTODO ATUALIZADO PARA INCLUIR PENDENTE DE AUTORIZAÇÃO
        getStatusTextoDetalhado(status, percentual) {
            const statusMap = {
                'CONCLUIDA': 'Concluída (100%)',
                'EM_ANDAMENTO': `Em Andamento (${percentual}%)`,
                'PENDENTE': 'Pendente (0%)',
                'PENDENTE_AUTORIZACAO_TERCEIRO': 'Pendente de Autorização (0%)', // ✅ NOVO STATUS
                'SEM_DADOS': 'Sem Dados',
                'ERRO': 'Erro',
                'INDEFINIDO': 'Indefinido',
                'SEM_MOVIMENTACAO': 'Sem Movimentação (0%)'
            };
            return statusMap[status] || status;
        }
    }
}
</script>

<style scoped>
.bg-gradient-primary {
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}
</style>